/**
 * Trait Gap Interpretation Module - Vue Version
 *
 * DEPRECATED (APR-921): This file is being phased out in favor of backend V2 data.
 *
 * When PERSONALITY_MATCH_V2_ENABLED=true, the backend provides normalized data
 * in match.personality_v2 payload. This eliminates the need for frontend
 * normalization and coverage calculation.
 *
 * This file is kept for:
 * - Backward compatibility when V2 flag disabled
 * - Standalone personality comparisons outside application context
 * - Legacy components not yet migrated to V2
 *
 * Related: APR-919 (backend V2 source of truth), APR-920 (consume V2 score)
 *
 * ---
 *
 * Provides interpretations for personality trait coverage gaps
 * when matching applicants to job requirements.
 *
 * Uses translation system instead of hardcoded locale files.
 *
 * Thresholds:
 * - < 50% coverage = Gap (primary concern)
 * - 50-69% coverage = Borderline (development area)
 * - >= 70% coverage = Sufficient (no interpretation needed)
 */

import { TRAITS, BASELINE } from './personalityConstants.js'

/**
 * Determines the severity level based on coverage percentage
 * @param {number} coverage - Coverage ratio (0 to 1+)
 * @returns {'gap'|'borderline'|'sufficient'}
 */
export const getCoverageSeverity = coverage => {
	if (coverage < 0.5) return 'gap'
	if (coverage < 0.7) return 'borderline'
	return 'sufficient'
}

/**
 * Gets interpretation for a single trait based on coverage
 * @param {string} traitKey - The trait identifier (e.g., 'supporter')
 * @param {number} coverage - Coverage ratio (0 to 1+)
 * @param {Function} $t - Vue i18n translation function
 * @returns {Object|null} - Interpretation object or null if sufficient coverage
 */
export const getTraitInterpretation = (traitKey, coverage, $t) => {
	const severity = getCoverageSeverity(coverage)

	if (severity === 'sufficient') {
		return null
	}

	// Get trait name from translation system
	const traitName = $t(`personality_results.trait.${traitKey}.name`)

	// Get interpretation content from translation system
	const interpretation = {
		whatTheyCanDo: $t(
			`personality_results.trait_gap.${traitKey}.${severity}.what_they_can_do`
		),
		whereTheyllStruggle: $t(
			`personality_results.trait_gap.${traitKey}.${severity}.where_theyll_struggle`
		)
	}

	// Get labels from translation system
	const label =
		severity === 'gap'
			? $t('personality_results.trait_gap.labels.primary_gap')
			: $t('personality_results.trait_gap.labels.development_area')

	return {
		trait: traitKey,
		traitName: traitName,
		coverage: coverage,
		coveragePercent: Math.round(coverage * 100),
		severity: severity,
		label: label,
		interpretation: interpretation
	}
}

/**
 * Analyzes all trait coverages and returns gaps and development areas
 * @param {Object} traitCoverages - Map of trait keys to coverage ratios
 * @param {Function} $t - Vue i18n translation function
 * @returns {Object} - Analysis results with gaps and development areas
 */
export const analyzeTraitGaps = (traitCoverages, $t) => {
	const gaps = []
	const borderline = []

	Object.entries(traitCoverages).forEach(([traitKey, coverage]) => {
		const interpretation = getTraitInterpretation(traitKey, coverage, $t)

		if (interpretation) {
			if (interpretation.severity === 'gap') {
				gaps.push(interpretation)
			} else {
				borderline.push(interpretation)
			}
		}
	})

	// Sort by coverage (lowest first = most critical)
	gaps.sort((a, b) => a.coverage - b.coverage)
	borderline.sort((a, b) => a.coverage - b.coverage)

	return {
		primaryGap: gaps[0] || null,
		additionalGaps: gaps.slice(1),
		developmentAreas: borderline,
		hasGaps: gaps.length > 0,
		hasDevelopmentAreas: borderline.length > 0,
		allSufficient: gaps.length === 0 && borderline.length === 0
	}
}

/**
 * Generates a human-readable summary of gap analysis
 * @param {Object} analysis - Result from analyzeTraitGaps()
 * @param {Function} $t - Vue i18n translation function
 * @returns {string} - Summary text
 */
export const generateGapSummary = (analysis, $t) => {
	if (analysis.allSufficient) {
		return $t('personality_results.trait_gap.summary.all_sufficient')
	}

	const parts = []

	if (analysis.primaryGap) {
		const gap = analysis.primaryGap
		const primaryGapText = $t(
			'personality_results.trait_gap.summary.primary_gap',
			{
				traitName: gap.traitName,
				coveragePercent: gap.coveragePercent,
				canDo: gap.interpretation.whatTheyCanDo,
				struggle: gap.interpretation.whereTheyllStruggle
			}
		)
		parts.push(primaryGapText)
	}

	if (analysis.additionalGaps.length > 0) {
		const additional = analysis.additionalGaps
			.map(g => `${g.traitName} (${g.coveragePercent}%)`)
			.join(', ')
		const additionalGapsText = $t(
			'personality_results.trait_gap.summary.additional_gaps',
			{
				list: additional
			}
		)
		parts.push(additionalGapsText)
	}

	if (analysis.developmentAreas.length > 0) {
		const devAreas = analysis.developmentAreas
			.map(d => `${d.traitName} (${d.coveragePercent}%)`)
			.join(', ')
		const developmentAreasText = $t(
			'personality_results.trait_gap.summary.development_areas',
			{
				list: devAreas
			}
		)
		parts.push(developmentAreasText)
	}

	return parts.join(' ')
}

/**
 * Formats interpretation for UI display
 * @param {Object} interpretation - Single trait interpretation from getTraitInterpretation()
 * @returns {Object|null} - Formatted for UI consumption
 */
export const formatForUI = interpretation => {
	if (!interpretation) return null

	return {
		trait: interpretation.traitName,
		coverage: `${interpretation.coveragePercent}%`,
		severity: interpretation.severity,
		label: interpretation.label,
		canDo: interpretation.interpretation.whatTheyCanDo,
		struggle: interpretation.interpretation.whereTheyllStruggle
	}
}

/**
 * Main entry point: Analyze trait coverages and return complete interpretation
 * @param {Object} applicantNorm - Applicant's normalized trait values
 * @param {Object} jobNorm - Job's normalized trait requirements
 * @param {Function} $t - Vue i18n translation function
 * @returns {Object} - Complete gap analysis with interpretations
 */
export const interpretTraitCoverage = (applicantNorm, jobNorm, $t) => {
	// Calculate coverage for each required trait
	const traitCoverages = {}

	Object.keys(jobNorm).forEach(traitKey => {
		if (jobNorm[traitKey] > 0) {
			traitCoverages[traitKey] =
				applicantNorm[traitKey] / jobNorm[traitKey]
		}
	})

	// Analyze gaps
	const analysis = analyzeTraitGaps(traitCoverages, $t)

	// Format for output
	return {
		coverages: traitCoverages,
		primaryGap: formatForUI(analysis.primaryGap),
		additionalGaps: analysis.additionalGaps
			.map(g => formatForUI(g))
			.filter(g => g !== null),
		developmentAreas: analysis.developmentAreas
			.map(d => formatForUI(d))
			.filter(d => d !== null),
		summary: generateGapSummary(analysis, $t),
		flags: {
			hasGaps: analysis.hasGaps,
			hasDevelopmentAreas: analysis.hasDevelopmentAreas,
			allSufficient: analysis.allSufficient
		}
	}
}

/**
 * Enhanced gap analysis with personality breakdown integration
 * @param {Object} applicantBreakdown - Applicant's personality breakdown
 * @param {Object} jobBreakdown - Job's personality breakdown
 * @param {Function} $t - Vue i18n translation function
 * @returns {Object} - Complete gap analysis with normalized values
 */
export const analyzePersonalityGap = (applicantBreakdown, jobBreakdown, $t) => {
	// Normalize both breakdowns
	const normalizeBreakdown = breakdown => {
		const normalized = {}
		TRAITS.forEach(trait => {
			normalized[trait] = (breakdown[trait] || 0) / BASELINE
		})
		return normalized
	}

	const applicantNorm = normalizeBreakdown(applicantBreakdown)
	const jobNorm = normalizeBreakdown(jobBreakdown)

	return interpretTraitCoverage(applicantNorm, jobNorm, $t)
}

/**
 * Get gap severity indicator for UI
 * @param {string} severity - 'gap' or 'borderline'
 * @param {Function} $t - Vue i18n translation function
 * @returns {Object} - UI indicator data
 */
export const getGapSeverityIndicator = (severity, $t) => {
	const indicators = {
		gap: {
			color: 'red',
			icon: 'exclamation-triangle',
			label: $t('personality_results.trait_gap.labels.primary_gap'),
			class: 'gap-indicator'
		},
		borderline: {
			color: 'yellow',
			icon: 'exclamation-circle',
			label: $t('personality_results.trait_gap.labels.development_area'),
			class: 'borderline-indicator'
		}
	}

	return indicators[severity] || null
}

/**
 * Get development recommendations based on gap analysis
 * @param {Object} analysis - Gap analysis result
 * @param {Function} $t - Vue i18n translation function
 * @returns {Array} - Array of recommendation objects
 */
export const getDevelopmentRecommendations = (analysis, $t) => {
	const recommendations = []

	// Add primary gap recommendations
	if (analysis.primaryGap) {
		recommendations.push({
			type: 'primary',
			trait: analysis.primaryGap.trait,
			title: $t('personality_results.recommendations.primary_focus', {
				trait: analysis.primaryGap.trait
			}),
			description: $t(
				`personality_results.recommendations.${analysis.primaryGap.trait}.development_focus`
			),
			priority: 'high'
		})
	}

	// Add development area recommendations
	analysis.developmentAreas.forEach(area => {
		recommendations.push({
			type: 'development',
			trait: area.trait,
			title: $t('personality_results.recommendations.enhance_skill', {
				trait: area.trait
			}),
			description: $t(
				`personality_results.recommendations.${area.trait}.enhancement_tip`
			),
			priority: 'medium'
		})
	})

	return recommendations
}

// 4-way coverage bucketing for the comparison gaps view.
// Keeps classification pure; the Vue component decorates names/signals for rendering.
export const BUCKET_GAP = 0.5
export const BUCKET_BORDERLINE = 0.7
export const BUCKET_STRENGTH = 1.2 // v1 threshold; v2 passes 1.0 (V2_STRENGTH)

export const classifyCoverages = (
	coverages,
	jobTraits = {},
	strengthThreshold = BUCKET_STRENGTH
) => {
	if (!coverages) {
		return { gaps: [], borderline: [], adequate: [], strengths: [] }
	}

	const items = Object.entries(coverages)
		.filter(([trait]) => (jobTraits[trait] ?? 0) > 0)
		.map(([trait, coverage]) => {
			let severity = 'adequate'

			if (coverage < BUCKET_GAP) {
				severity = 'gap'
			} else if (coverage < BUCKET_BORDERLINE) {
				severity = 'borderline'
			} else if (coverage >= strengthThreshold) {
				severity = 'strength'
			}

			return {
				key: trait,
				coverage,
				coveragePercent: Math.round(coverage * 100),
				severity
			}
		})

	return {
		gaps: items
			.filter(i => i.severity === 'gap')
			.sort((a, b) => a.coverage - b.coverage),
		borderline: items
			.filter(i => i.severity === 'borderline')
			.sort((a, b) => a.coverage - b.coverage),
		adequate: items
			.filter(i => i.severity === 'adequate')
			.sort((a, b) => b.coverage - a.coverage),
		strengths: items
			.filter(i => i.severity === 'strength')
			.sort((a, b) => b.coverage - a.coverage)
	}
}

// Export constants for external use
export const COVERAGE_THRESHOLDS = {
	GAP: 0.5,
	BORDERLINE: 0.7,
	SUFFICIENT: 0.7
}

// Default export for convenience
export default {
	getCoverageSeverity,
	getTraitInterpretation,
	analyzeTraitGaps,
	generateGapSummary,
	formatForUI,
	interpretTraitCoverage,
	analyzePersonalityGap,
	getGapSeverityIndicator,
	getDevelopmentRecommendations,
	classifyCoverages,
	COVERAGE_THRESHOLDS
}
